import * as R from "ramda";
import React, {FC, useEffect, useMemo, useState} from "react";
import {useLocation, useSearchParams} from "react-router-dom";
import moment from "moment/moment";

import {Dropdown} from "primereact/dropdown";

import SGrid, {SGridProps} from '../grid/grid.component';
import {DocumentEntityT} from "../../models/documents/documents-provider";
import {getUrlExtension} from "../../utils/file";
import {IconsEnum} from "../../constants/icons";
import {FileExtensionEnum, ICONS_MAP} from "../../definitions/common.definitions";
import {useDocumentType} from "../../models/document-type/document-type-provider";
import {useGet} from "../../transport/useGet";
import {EndpointsEnum} from "../../constants/endpoints";
import {Button} from 'primereact/button';
import copy from 'copy-to-clipboard';
import {useMessage} from '../../providers/message/message-provider';

export type DocumentManagerPropsT = {
  gridConfig?: SGridProps;
  initCategoryId?: number;
  initSubCategoryId?: number;
  disableCategory?: boolean;
  disableSubCategory?: boolean;
  year?: number;
}

const currentYear = new Date().getFullYear() + 1;
const years = Array.from({ length: currentYear - 2010 }, (v, i) => ({
  label: `${currentYear - i}`,
  value: currentYear - i,
}));

const getRawFilePath = (filePath: string) => {
  return filePath.replace('https://ess-storage.storage.yandexcloud.net/documents', `${window.location.origin}/download`);
}

const DocumentManager:FC<DocumentManagerPropsT> = (props) => {
  const {
    gridConfig,
    initCategoryId,
    initSubCategoryId,
    disableCategory,
    disableSubCategory,
    year,
  } = props;

  const [ category, setCategory ] = useState<number | undefined>(initCategoryId);
  const [ subCategory, setSubCategory ] = useState<number | undefined>(initSubCategoryId);
  const [ $year, set$Year ] = useState(years[1]);

  const location = useLocation();

  useEffect(() => {
    setCategory(initCategoryId);
    setSubCategory(initSubCategoryId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const [searchParams, setSearchParams] = useSearchParams();

  const {
    showMessage,
  } = useMessage();

  // Функция для обновления URL параметров
  const updateUrlParams = (newCategory?: number | null, newSubCategory?: number | null, newYear?: number | null) => {
    const newSearchParams = new URLSearchParams(searchParams);

    if (newCategory !== undefined && newCategory !== null) {
      newSearchParams.set('c', newCategory.toString());
    } else {
      newSearchParams.delete('c');
    }

    if (newSubCategory !== undefined && newSubCategory !== null) {
      newSearchParams.set('s', newSubCategory.toString());
    } else {
      newSearchParams.delete('s');
    }

    if (newYear !== undefined && newYear !== null) {
      newSearchParams.set('y', newYear.toString());
    } else {
      newSearchParams.delete('y');
    }

    setSearchParams(newSearchParams, { replace: true });
  };

  useEffect(() => {
    setCategory((initCategoryId));
  }, [initCategoryId]);

  useEffect(() => {
    setSubCategory((initSubCategoryId));
  }, [initSubCategoryId]);

  useEffect(() => {
    if (year) {
      set$Year({ value: year, label: `${year}` });
    }
  }, [year]);

  const {
    categories,
    subCategories,
    isLoading,
  } = useDocumentType();
  const documentTypeId = subCategory || category;
  const {
    data: documentsData = [],
    isLoading: documentsIsLoading,
  } = useGet<DocumentEntityT[]>({
    key: R.isNil(documentTypeId) ? null : EndpointsEnum.DOCUMENTS,
    auth: false,
    params: {
      documentTypeId,
      year: $year?.value,
    }
  });

  const sortedDocuments = useMemo(() => {
    return [...documentsData].sort((a, b) =>
      // @ts-expect-error
      new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime()
    );
  }, [documentsData]);

  const asIconElement = (doc: DocumentEntityT) => {
    const ext = getUrlExtension(doc.fileUrl);
    let iconName = IconsEnum.FILE;
    if (!R.isNil(ext)) {
      iconName = ICONS_MAP[ext as FileExtensionEnum] ?? IconsEnum.FILE;
    }

    return <i className={`pi ${iconName}`}></i>;
  }

  const columns = [
    {
      header: 'Наименование документа',
      field: 'name',
      body: (itm: DocumentEntityT) => (
        <Button
          label={itm.name || itm.fileName}
          style={{ textAlign: 'left' }}
          link
          onClick={() =>  window.open(getRawFilePath(itm.fileUrl), '_blank')}
        />
      ),
    },
    {
      style: { width: '200px' },
      body: (itm: DocumentEntityT) => (itm.year! >= 2025 ? <span>{moment.utc(itm.publishedDate).local().format('DD MMMM YYYY')}</span> : <span>На sibseti.su</span>),
      header: 'Дата публикации',
    },
    {
      field: 'icon',
      header: 'Ссылка',
      body: (itm: DocumentEntityT) => (
        <Button
          onClick={() => {
            copy(getRawFilePath(itm.fileUrl).replace(window.location.origin, 'https://eseti.site'));
            showMessage({ message: 'Ссылка скопирована в буфер обмена' });
          }}
          icon="pi pi-link"
          rounded
          text
          aria-label="Filter"
        />
      ),
      style: { width: '150px' },
    },
    {
      field: 'icon',
      header: 'Тип документа',
      body: asIconElement,
      style: { width: '150px' },
    },
  ];

  return (
    <div>
      <div
        className={'my-2'}
      >
        <Dropdown
          value={category}
          onChange={(e) => {
            setCategory(e.value);
            updateUrlParams(e.value, subCategory, $year?.value);
          }}
          optionLabel={'name'}
          optionValue={'id'}
          options={categories}
          showClear={true}
          placeholder={'Категория'}
          className={'w-full md:w-14rem ml-2 mt-2'}
          emptyMessage={'Нет данных'}
          loading={isLoading}
          disabled={disableCategory}
        />
        {
          !R.isEmpty(subCategories) && (
            <Dropdown
              value={subCategory}
              options={subCategories(category)}
              optionLabel={'name'}
              optionValue={'id'}
              showClear={true}
              onChange={(e) => {
                setSubCategory(e.value);
                updateUrlParams(category, e.value, $year?.value);
              }}
              placeholder={'Подкатегория'}
              className={'w-full md:w-14rem ml-2 mt-2'}
              emptyMessage={'Нет данных'}
              loading={isLoading}
              disabled={disableSubCategory}
            />
          )
        }
        <Dropdown
          value={$year}
          onChange={(e) => {
            set$Year(e.value);
            updateUrlParams(category, subCategory, e.value?.value);
          }}
          options={years}
          multiple={false}
          optionValue={'id'}
          showClear={true}
          placeholder={'Год'}
          className={'w-full md:w-8rem ml-2 mt-2'}
          emptyMessage={'Нет данных'}
          loading={isLoading}
        />
      </div>
      <SGrid
        dataKey={'id'}
        loading={documentsIsLoading || isLoading}
        value={sortedDocuments}
        columns={columns}
        emptyMessage={'Выберите категории и период для получения документов, либо перейдите на sibseti.su за более ранними документами'}
        {...gridConfig}
      />
    </div>
  );
}

export default DocumentManager;
